import { isUndefined, isEmpty } from '../utils/functions.js';
import { LayoutBase } from './LayoutBase.js';
import { LayoutState } from './LayoutState.js';
import { TreeLayoutProcessor } from './TreeLayoutProcessor.js';
import { DiagramToHyperTreeAdapter } from '../math/Graph.js';

/**
 * The various tree layout algorithms.
 *
 * @type {*}
 */
class TreeLayout extends LayoutBase {
    constructor(diagram) {
        super();
        if (isUndefined(diagram)) {
            throw new Error('No diagram specified.');
        }
        this.diagram = diagram;
    }
    /**
     * Arranges the diagram in a tree-layout with the specified options and tree subtype.
     */
    layout(options) {
        this.transferOptions(options);
        // transform the diagram into a Graph
        const adapter = new DiagramToHyperTreeAdapter(this.diagram);
        /**
         * The Graph reduction from the given diagram.
         *
         * @type {*}
         */
        this.graph = adapter.convert();
        const finalNodeSet = this.layoutComponents();
        // note that the graph contains the original data and
        // the components are another instance of nodes referring to the same set of shapes
        return new LayoutState(this.diagram, finalNodeSet);
    }
    layoutComponents() {
        if (this.graph.isEmpty()) {
            return;
        }
        // split into connected components
        const components = this.graph.getConnectedComponents();
        if (isEmpty(components)) {
            return;
        }
        const layout = new TreeLayoutProcessor(this.options);
        const trees = [];
        // find a spanning tree for each component
        for (let i = 0; i < components.length; i++) {
            const component = components[i];
            const treeGraph = this.getTree(component);
            if (!treeGraph) {
                throw new Error('Failed to find a spanning tree for the component.');
            }
            const root = treeGraph.root;
            const tree = treeGraph.tree;
            layout.layout(tree, root);
            trees.push(tree);
        }
        return this.gridLayoutComponents(trees);
    }
    /**
     * Gets a spanning tree (and root) for the given graph.
     * Ensure that the given graph is connected!
     *
     * @param graph
     * @returns {*} A literal object consisting of the found root and the spanning tree.
     */
    getTree(graph) {
        let root = null;
        if (this.options.roots && this.options.roots.length > 0) {
            for (let i = 0, len = graph.nodes.length; i < len; i++) {
                const node = graph.nodes[i];
                for (let j = 0; j < this.options.roots.length; j++) {
                    const givenRootShape = this.options.roots[j];
                    if (givenRootShape === node.associatedShape) {
                        root = node;
                        break;
                    }
                }
            }
        }
        if (!root) {
            // finds the most probable root on the basis of the longest path in the component
            root = graph.root();
            // should not happen really
            if (!root) {
                throw new Error('Unable to find a root for the tree.');
            }
        }
        return this.getTreeForRoot(graph, root);
    }
    getTreeForRoot(graph, root) {
        const tree = graph.getSpanningTree(root);
        if (isUndefined(tree) || tree.isEmpty()) {
            return null;
        }
        return {
            tree: tree,
            root: tree.root()
        };
    }
}

export { TreeLayout };
